import { cn } from "@/lib/utils";
import type { FeaturedBanner as FeaturedBannerType } from "@/types/database";
import Link from "next/link";
import { useState } from "react";
import "./featured-banner-container.css";
import {
  FEATURED_BANNER_COPY_CLASS,
  FEATURED_BANNER_DESCRIPTION_CLASS,
  FEATURED_BANNER_FRAME_CLASS,
  FEATURED_BANNER_ICON_CLASS,
  FEATURED_BANNER_ICON_IMAGE_CLASS,
} from "./featured-banner-description-classes";
import { normalizeFeaturedBannerTextColor } from "./featured-banner-text-color";

interface FeaturedBannerV2Props {
  banner: FeaturedBannerType;
  className?: string;
}

export function FeaturedBannerV2({ banner, className = "" }: FeaturedBannerV2Props) {
  const [bgLoaded, setBgLoaded] = useState(false);
  const [iconLoaded, setIconLoaded] = useState(!banner.icon_url);
  const isFullyLoaded = bgLoaded && iconLoaded;
  const textColor = normalizeFeaturedBannerTextColor(banner.text_color);

  return (
    <Link href={banner.destination_url} className={`block ${className} group`}>
      <div
        className={cn(FEATURED_BANNER_FRAME_CLASS, "border-4 border-black")}
        style={{ aspectRatio: "960/320" }}
      >
        {/* Background image skeleton */}
        <div
          className={`absolute inset-0 bg-brand-purple transition-opacity duration-300 ${bgLoaded ? "opacity-0" : "opacity-100"}`}
        >
          <div className="w-full h-full shimmer-gradient" />
        </div>

        {/* Background image */}
        <img
          src={banner.image_url}
          alt={banner.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          onLoad={() => setBgLoaded(true)}
        />

        {/* Content container */}
        <div
          className={`absolute inset-0 px-2 flex items-center transition-all duration-700 ${isFullyLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}
          style={{ transformOrigin: "center" }}
        >
          <div className={FEATURED_BANNER_COPY_CLASS} style={{ color: textColor }}>
            {/* Icon/Logo */}
            {banner.icon_url && (
              <div className={`${FEATURED_BANNER_ICON_CLASS} animate-float`}>
                <img
                  src={banner.icon_url}
                  alt={`${banner.title} icon`}
                  className={FEATURED_BANNER_ICON_IMAGE_CLASS}
                  onLoad={() => setIconLoaded(true)}
                />
              </div>
            )}

            {/* Description */}
            {banner.description && (
              <p className={FEATURED_BANNER_DESCRIPTION_CLASS} style={{ color: textColor }}>
                {banner.description}
              </p>
            )}
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-float { animation: float 3s ease-in-out infinite; }
        .shimmer-gradient {
          position: relative;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, rgb(var(--brand-purple) / 0.9) 0%, rgb(var(--brand-indigo-soft) / 0.15) 50%, rgb(var(--brand-purple) / 0.9) 100%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite linear;
        }
        @keyframes shimmer {
          0% { background-position: -100% 0; }
          100% { background-position: 100% 0; }
        }
      `}</style>
    </Link>
  );
}

export default FeaturedBannerV2;
